import { useState, useEffect, useCallback, useMemo } from 'react';
import { quotesAPI, catalogAPI, pricingAPI } from '../../services/api';
import { fmt } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';
import SearchSelect from '../common/SearchSelect';
import { sendWithCrossFamilyAck } from '../../utils/crossFamily';
import toast from 'react-hot-toast';


// =============================================================================
// Domain note on role-based editability (2026-05-17):
//
// labor_persons and labor_hours are OPERATIONAL fields that vendors do NOT
// decide. They are auto-computed from catalog data (weight_class) and the
// labor scale (driven by sqft). Their values flow downstream to Operations.
//
// Editability policy:
//   - vendor     -> read-only (sees the value as information)
//   - manager+   -> can override via "Override" button
//   - estimator  -> FUTURE: dedicated review view for Melissa Albo
//
// The PDF sent to the client never displays these fields - they appear only
// internally in the line summary and in the Operations endpoint response.
// =============================================================================

// -- Design picker: thumbnail card -------------------------------------------
// Definido FUERA del componente principal (antes vivia adentro y React lo
// remontaba en cada render -> parpadeo de imagen y perdida de estado).
function DesignCard({ design, selected, onSelect }) {
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => { setImgOk(true); }, [design.image_url]);
  return (
    <div
      className={`design-card${selected ? ' selected' : ''}`}
      onClick={() => onSelect(design)}
      title={design.name}
    >
      {design.image_url && imgOk ? (
        <img src={design.image_url} alt={design.name} loading="lazy"
          onError={() => setImgOk(false)} />
      ) : (
        <div className="design-card-placeholder">{(design.name || '?').slice(0, 2).toUpperCase()}</div>
      )}
      <div className="design-card-name">{design.name}</div>
    </div>
  );
}

// -- Selected design detail: big photo + variants + product info -------------
function shapeFromLabel(label) {
  const s = (label || '').toLowerCase();
  if (s.includes('eyebrow')) return 'eyebrow';
  if (s.includes('round'))   return 'round';
  if (s.includes('arch'))    return 'arch';
  if (s.includes('transom')) return 'transom';
  if (s.includes('sidelight')) return 'sidelight';
  if (s.includes('square'))  return '';
  return null;
}

function DesignDetail({ design, showCostInfo, onShape }) {
  const [activeUrl, setActiveUrl] = useState(design.image_url);
  const variants = design.image_variants || [];

  useEffect(() => { setActiveUrl(design.image_url); }, [design.id, design.image_url]);

  // list_price_sf es dato de COSTO (lista interna) - solo manager/admin
  const meta = [
    showCostInfo && design.list_price_sf != null && `${fmt.currency(design.list_price_sf)}/sf list`,
    design.weight_class && `${design.weight_class} weight`,
    showCostInfo && design.weight_lbs_per_sqft != null && `${parseFloat(design.weight_lbs_per_sqft).toFixed(1)} lbs/sf`,
  ].filter(Boolean).join(' - ');

  return (
    <div className="design-detail">
      {activeUrl && (
        <img className="design-detail-img" src={activeUrl} alt={design.name}
          onError={e => { e.target.style.visibility = 'hidden'; }} />
      )}
      <div className="design-detail-info">
        <div className="design-detail-name">{design.name}</div>
        {meta && <div className="design-detail-meta">{meta}</div>}
        {design.description && (
          <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
            {design.description}
          </div>
        )}
        {variants.length > 0 && (
          <div className="design-variants">
            {variants.map((v, i) => (
              <button key={i} type="button"
                className={`design-variant-btn${activeUrl === v.url ? ' active' : ''}`}
                onClick={() => {
                  setActiveUrl(v.url);
                  const sh = shapeFromLabel(v.label);
                  if (sh !== null && onShape) onShape(sh);
                }}>
                {v.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function IronLineModal({ quoteId, installer = 'buiria', editData = null, crossFamilyAck = false, onClose, onSaved }) {
  const { isManager } = useAuth();   // true for manager/admin
  const isEdit = !!editData?.id;

  const [cats, setCats]             = useState([]);
  const [designs, setDesigns]       = useState([]);
  const [glassTypes, setGlassTypes] = useState([]);
  const [colors, setColors] = useState([]);
  const [handles, setHandles] = useState([]);
  const [liteDefaults, setLiteDefaults] = useState([]);
  const [kits, setKits]             = useState([]);
  const [scales, setScales]         = useState([]);
  const [calc, setCalc]             = useState(null);
  const [saving, setSaving]         = useState(false);
  const [designQuery, setDesignQuery] = useState('');
  // En edicion, el kit guardado cuenta como eleccion manual (no auto-pisar)
  const [kitTouched, setKitTouched]   = useState(!!editData?.kit_id);

  const noInstall = installer === 'none';

  const [form, setForm] = useState(() => editData ? {
    line_number: editData.line_number || '', location: editData.location || '',
    category_id: editData.category_id != null ? String(editData.category_id) : '',
    design_id:   editData.design_id   != null ? String(editData.design_id)   : '',
    width_in:    editData.width_in  ?? '', height_in: editData.height_in ?? '',
    disc_pct:    editData.disc_pct ? Math.round(parseFloat(editData.disc_pct) * 10000) / 100 : 0,
    glass_type_id:   editData.glass_type_id != null ? String(editData.glass_type_id) : '',
    glass_width_in:  editData.glass_width_in  ?? '',
    glass_height_in: editData.glass_height_in ?? '',
    is_remodel: !!editData.is_remodel,
    kit_id:     editData.kit_id != null ? String(editData.kit_id) : '',
    trim_lf:    editData.trim_lf ?? 0, has_trim: !!editData.has_trim, trim_sides: editData.trim_sides ?? 2, deadbolt_type: editData.deadbolt_type || 'none',
    lites_delta: editData.lites_delta ?? 0,
    template_charge: !!editData.template_charge, glass_notch: !!editData.glass_notch,
    notch_qty: editData.notch_qty ?? 0, template_qty: editData.template_qty ?? 0,
    top_shape: editData.top_shape ?? '',
    has_transom: !!editData.has_transom,
    transom_width_in: editData.transom_width_in ?? '', transom_height_in: editData.transom_height_in ?? '',
    transom_top_shape: editData.transom_top_shape ?? '',
    has_sidelights: !!editData.has_sidelights,
    sidelight_left_width_in: editData.sidelight_left_width_in ?? '',
    sidelight_right_width_in: editData.sidelight_right_width_in ?? '',
    jamb_width_in: editData.jamb_width_in ?? '', has_nailfin: !!editData.has_nailfin,
    hinge_type: editData.hinge_type || 'hinged',
    include_labor: !!editData.include_labor,
    labor_scale_id: editData.labor_scale_id != null ? String(editData.labor_scale_id) : '',
    labor_manual: editData.labor_status === 'manual' || editData.labor_status === 'approved',
    labor_persons: editData.labor_persons ?? null,
    labor_hours: editData.labor_hours ?? '',
    crew_persons: editData.crew_persons ?? '', crew_hours: editData.crew_hours ?? '',
    reinforce_persons: editData.reinforce_persons ?? '', reinforce_hours: editData.reinforce_hours ?? '',
    jamb_labor_persons: editData.jamb_labor_persons ?? '', jamb_labor_hours: editData.jamb_labor_hours ?? '',
    trip_fee: editData.trip_fee ?? 0, gas_fee: editData.gas_fee ?? 0,
    transport_fee: editData.transport_fee ?? 150,
    finish: editData.finish || '', opens: editData.opens || '',
    handle: editData.handle || '',
    handle_side: editData.handle_side || '', active_side: editData.active_side || '',
  } : {
    line_number: '', location: '',
    category_id: '', design_id: '',
    width_in: '', height_in: '',
    disc_pct: 0,
    glass_type_id: '1', glass_width_in: '', glass_height_in: '',
    is_remodel: false, kit_id: '',
    trim_lf: 0, has_trim: false, trim_sides: 2, deadbolt_type: 'none',
    lites_delta: 0,
    template_charge: false, glass_notch: false,
    notch_qty: 0, template_qty: 0,
    top_shape: '',
    has_transom: false, transom_width_in: '', transom_height_in: '', transom_top_shape: '',
    has_sidelights: false, sidelight_left_width_in: '', sidelight_right_width_in: '',
    jamb_width_in: '', has_nailfin: false,
    hinge_type: 'hinged',
    include_labor: false, labor_scale_id: '',
    labor_manual: false,
    labor_persons: null,
    labor_hours: '',
    crew_persons: '', crew_hours: '',
    reinforce_persons: '', reinforce_hours: '',
    jamb_labor_persons: '', jamb_labor_hours: '',
    trip_fee: 0, gas_fee: 0,
    transport_fee: 150,
    finish: '', opens: '', handle: '',
    handle_side: '', active_side: '',
  });

  // B.1: categoria doble? (decide HANDLE SIDE vs ACTIVE SIDE, mutex)
  const b1Cat = cats.find(x => String(x.id) === String(form.category_id));
  const b1IsDouble = /DBL|double|doble/i.test(`${b1Cat?.code || ''} ${b1Cat?.name || ''}`);

  // ---- migr 076: aprobacion de labor por linea (G.4 / G.7) ----------
  // Estado local: editData trae el status al abrir; el POST lo actualiza
  // sin recargar. Una linea nueva no tiene id -> no se puede aprobar
  // hasta guardarla (el endpoint pega contra /lines/iron/:lineId).
  const [laborApproval, setLaborApproval] = useState({
    status:      editData?.labor_status      || 'derived',
    approved_by: editData?.labor_approved_by || null,
    approved_at: editData?.labor_approved_at || null,
    by_name:     null,
  });
  const [approving, setApproving] = useState(false);
  const isLaborApproved = laborApproval.status === 'approved';
  const toggleLaborApproval = async () => {
    if (!editData?.id || approving) return;
    const next = !isLaborApproved;
    setApproving(true);
    try {
      const r = await quotesAPI.approveIronLabor(quoteId, editData.id, next);
      setLaborApproval({
        status:      r.data.labor_status,
        approved_by: r.data.labor_approved_by,
        approved_at: r.data.labor_approved_at,
        by_name:     r.data.approved_by_name,
      });
      toast.success(next ? 'Labor approved' : 'Approval removed');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not update approval');
    } finally {
      setApproving(false);
    }
  };

  useEffect(() => {
    catalogAPI.ironCategories().then(r => setCats(r.data));
    catalogAPI.ironGlass().then(r => setGlassTypes(r.data));
    catalogAPI.ironColors().then(r => setColors(r.data));
    catalogAPI.ironHandles().then(r => setHandles(r.data));
    catalogAPI.ironLiteDefaults().then(r => setLiteDefaults(r.data));
    catalogAPI.ironKits().then(r => setKits(r.data));
    catalogAPI.ironLaborScales().then(r => setScales(r.data));
  }, []);

  useEffect(() => {
    if (form.category_id) {
      catalogAPI.ironDesigns(form.category_id).then(r => setDesigns(r.data));
    } else {
      setDesigns([]);
    }
  }, [form.category_id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // H.16 (Mau 2026-07-20): el trim cubre TODO el vano, no solo la puerta.
  // Antes el LF salia de (ancho + 2*alto)/12 de la puerta sola, asi que una
  // entrada con sidelights cobraba de menos. Ahora usa el bounding box del
  // ensamble -- el mismo que ya usa la jamba desde H.1a.
  // Fuente unica: las 3 zonas que calculaban el LF llamaban a esta funcion.
  const trimLfFrom = (f) => {
    const w = parseFloat(f.width_in)  || 0;
    const h = parseFloat(f.height_in) || 0;
    const sl = f.has_sidelights
      ? (parseFloat(f.sidelight_left_width_in) || 0) + (parseFloat(f.sidelight_right_width_in) || 0) : 0;
    const tr = f.has_transom ? (parseFloat(f.transom_height_in) || 0) : 0;
    const aw = w + sl, ah = h + tr;
    const sides = parseInt(f.trim_sides) || 2;
    return { aw, ah, sides, lf: ((aw + 2 * ah) / 12) * sides,
             hasAddons: (sl > 0 || tr > 0) };
  };

  // Mientras el vendedor no teclee el LF a mano, se recalcula solo al cambiar
  // medidas o add-ons (patron kitTouched / labor_hours_overridden).
  const [trimTouched, setTrimTouched] = useState(false);

  // Selected category (for kit auto-select)
  const selectedCat = cats.find(c => String(c.id) === String(form.category_id));

  // -- Kit automatico (regla Mau 2026-06-06) --
  // El kit lo determina (nueva/remo) x (sencilla/doble). Se auto-selecciona
  // si hay UN kit que cumpla ambos criterios; el Vendedor puede cambiarlo
  // a mano (eso desactiva el auto para esta linea).
  useEffect(() => {
    if (kitTouched || !kits.length || !selectedCat) return;
    const catTxt = `${selectedCat.code || ''} ${selectedCat.name || ''}`;
    const isDbl = /DBL|double|doble/i.test(catTxt);
    const isSgl = /SGL|single|sencill/i.test(catTxt);
    if (!isDbl && !isSgl) return;   // PIVOT/GATE/SLIDING/CUSTOM: sin auto
    const constrRe = form.is_remodel ? /remo/i : /new|nueva|n\.?\s?c\b/i;
    const doorRe   = isDbl ? /dbl|double|doble/i : /sgl|single|sencill/i;
    const matches = kits.filter(k => constrRe.test(k.name || '') && doorRe.test(k.name || ''));
    if (matches.length === 1) {
      setForm(f => ({ ...f, kit_id: String(matches[0].id) }));
    }
  }, [form.category_id, form.is_remodel, kits, kitTouched, selectedCat]); // eslint-disable-line

  // Al elegir forma del top, pre-llena template con el sugerido del historico (editable).
  useEffect(() => {
    if (!form.top_shape || !liteDefaults.length) return;
    const d = liteDefaults.find(x => x.top_shape === form.top_shape);
    if (d) set('template_qty', d.template_default);
  }, [form.top_shape, liteDefaults]); // eslint-disable-line

  // Selected design (for weight_class lookup + detail panel)
  const selectedDesign = designs.find(d => d.id === parseInt(form.design_id));
  // migr 071: el crew se sugiere con el peso del FIERRO. El vidrio viaja y se
  // instala aparte, asi que no cuenta para la cuadrilla que levanta la puerta.
  const estWeight = (calc?.weight_iron != null)
    ? calc.weight_iron
    : (selectedDesign?.weight_lbs_per_sqft && form.width_in && form.height_in)
      ? parseFloat(selectedDesign.weight_lbs_per_sqft) * (parseFloat(form.width_in) * parseFloat(form.height_in) / 144)
      : null;

  // H.17 (2026-07-23): la derivacion de labor vive en el BACKEND (migr 073-077).
  // Aqui habia dos useEffect que la calculaban por su cuenta: uno por PESO
  // (suggestCrewSize, regla Eugenio 2026-05) y otro por sqft leyendo hours_min.
  // Los dos quedaron obsoletos:
  //   - hours_min ahora son horas-EQUIVALENTE de un equipo de dos, no horas
  //     del renglon base (migr 075).
  //   - la frontera del modal era `< max` y la del motor `<= max`: una puerta
  //     de 48 sqft caia en Medium en pantalla y en Standard en el PDF.
  //   - el crew ya no depende del peso ni del pivote (validado vs 5 cotizaciones
  //     de Melissa 2026-07-23: GUADALUPE y BROOKS son pivote y llevan el crew
  //     normal de su tramo).
  const laborDetail = calc?.labor_detail || null;

  // Horas-equivalente de un equipo de dos -- columna I del Excel de Melissa.
  // H.1 (2026-07-23): forma GENERAL. La version previa sumaba crew_hours crudo,
  // correcto SOLO si crew_persons === 2. Melissa escala el crew base arriba de
  // 80 sqft (caso PIGGUE) y ahi el total salia corto.
  const eqHours = (p, h) => ((parseFloat(p) || 0) * (parseFloat(h) || 0)) / 2;
  const numEq = (a, b) => (parseFloat(a) || 0) === (parseFloat(b) || 0);
  const laborHoursEquiv = laborDetail
    ? Math.round((eqHours(laborDetail.crew_persons, laborDetail.crew_hours)
        + eqHours(laborDetail.reinforce_persons, laborDetail.reinforce_hours)
        + eqHours(laborDetail.jamb_labor_persons, laborDetail.jamb_labor_hours)) * 10) / 10
    : null;

  // Toggle manual APAGADO: los seis campos siguen pegados a la derivacion del
  // motor, asi que al encenderlo ya traen lo derivado (delta cero, sin sembrado
  // aparte). ENCENDIDO: el efecto no toca nada y respeta lo tecleado.
  // El comparador cubre los NUEVE campos que escribe: runCalc depende de [form],
  // asi que escribir algo que no se compara deja el ciclo abierto (una llamada
  // al backend cada 500ms). numEq normaliza null/''/undefined a 0 en ambos lados.
  useEffect(() => {
    if (!laborDetail || form.labor_manual) return;
    setForm(f => {
      const same = numEq(f.labor_hours, laborHoursEquiv)
                && String(f.labor_scale_id) === String(laborDetail.labor_scale_id)
                && numEq(f.labor_persons, laborDetail.total_persons)
                && numEq(f.crew_persons, laborDetail.crew_persons)
                && numEq(f.crew_hours, laborDetail.crew_hours)
                && numEq(f.reinforce_persons, laborDetail.reinforce_persons)
                && numEq(f.reinforce_hours, laborDetail.reinforce_hours)
                && numEq(f.jamb_labor_persons, laborDetail.jamb_labor_persons)
                && numEq(f.jamb_labor_hours, laborDetail.jamb_labor_hours);
      return same ? f : { ...f,
        labor_hours:        laborHoursEquiv,
        labor_scale_id:     laborDetail.labor_scale_id,
        labor_persons:      laborDetail.total_persons,
        crew_persons:       laborDetail.crew_persons ?? '',
        crew_hours:         laborDetail.crew_hours ?? '',
        reinforce_persons:  laborDetail.reinforce_persons ?? '',
        reinforce_hours:    laborDetail.reinforce_hours ?? '',
        jamb_labor_persons: laborDetail.jamb_labor_persons ?? '',
        jamb_labor_hours:   laborDetail.jamb_labor_hours ?? '',
      };
    });
  }, [laborDetail, laborHoursEquiv, form.labor_manual]); // eslint-disable-line

  // H.16 / D.15: mientras el LF no se haya tecleado a mano, sigue a las
  // medidas y a los add-ons. Sin esto, marcar Trim y DESPUES agregar
  // sidelights dejaba el LF corto (el bug que H.16 corrige, por otra puerta).
  // Deps: todos los campos que lee trimLfFrom -- doctrina post-c5666df.
  useEffect(() => {
    if (!form.has_trim || trimTouched) return;
    setForm(f => {
      const lf = Math.round(trimLfFrom(f).lf * 10) / 10;
      return parseFloat(f.trim_lf) === lf ? f : { ...f, trim_lf: lf };
    });
  }, [form.has_trim, trimTouched, form.width_in, form.height_in, form.trim_sides,
      form.has_sidelights, form.sidelight_left_width_in, form.sidelight_right_width_in,
      form.has_transom, form.transom_height_in]); // eslint-disable-line

  // Designs filtered by search query (client-side, typeahead)
  const filteredDesigns = useMemo(() => {
    const q = designQuery.trim().toLowerCase();
    if (!q) return designs;
    return designs.filter(d => (d.name || '').toLowerCase().includes(q));
  }, [designs, designQuery]);

  const runCalc = useCallback(async () => {
    if (!form.design_id || !form.width_in || !form.height_in) { setCalc(null); return; }
    try {
      const { data } = await pricingAPI.calcIron({
        quote_id:       quoteId,   // H.12: ancla de fecha y NP; el server los resuelve
        design_id:      parseInt(form.design_id),
        width_in:       parseFloat(form.width_in),
        height_in:      parseFloat(form.height_in),
        disc_pct:       parseFloat(form.disc_pct || 0) / 100,
        glass_type_id:  form.glass_type_id ? parseInt(form.glass_type_id) : undefined,
        glass_width_in: form.glass_width_in ? parseFloat(form.glass_width_in) : undefined,
        glass_height_in:form.glass_height_in ? parseFloat(form.glass_height_in) : undefined,
        is_remodel:     form.is_remodel,
        kit_id:         form.kit_id ? parseInt(form.kit_id) : undefined,
        trim_lf:        parseFloat(form.trim_lf || 0),
        has_trim:        form.has_trim,
        trim_sides:      parseInt(form.trim_sides || 2),
        lites_delta:     parseInt(form.lites_delta || 0),
        deadbolt_type:  form.deadbolt_type,
        template_charge:form.template_charge,
        glass_notch:    form.glass_notch,
        notch_qty:      parseInt(form.notch_qty || 0),
        template_qty:   parseInt(form.template_qty || 0),
        top_shape:      form.top_shape || null,
        has_transom:    form.has_transom,
        transom_width_in:  form.transom_width_in ? parseFloat(form.transom_width_in) : undefined,
        transom_height_in: form.transom_height_in ? parseFloat(form.transom_height_in) : undefined,
        transom_top_shape: form.transom_top_shape || null,
        has_sidelights: form.has_sidelights,
        sidelight_left_width_in:  form.sidelight_left_width_in ? parseFloat(form.sidelight_left_width_in) : undefined,
        sidelight_right_width_in: form.sidelight_right_width_in ? parseFloat(form.sidelight_right_width_in) : undefined,
        jamb_width_in:  form.jamb_width_in ? parseFloat(form.jamb_width_in) : undefined,
        has_nailfin:    form.has_nailfin,
        include_labor:  form.include_labor,
        // H.17 (2026-07-23): labor lo deriva el BACKEND (migr 073-077) desde
        // iron_labor_scales por area del ensamble, epoca, remodel y jamb.
        // labor_hours solo viaja cuando hay override manual: si va siempre,
        // la derivacion no corre (regla D.4) y no llega labor_detail.
        labor_status:   form.labor_manual ? 'manual' : 'derived',
        labor_persons:  undefined,
        labor_hours:    undefined,
        crew_persons:        form.labor_manual ? parseInt(form.crew_persons) : undefined,
        crew_hours:          form.labor_manual ? parseFloat(form.crew_hours) : undefined,
        reinforce_persons:   form.labor_manual ? parseInt(form.reinforce_persons) : undefined,
        reinforce_hours:     form.labor_manual ? parseFloat(form.reinforce_hours) : undefined,
        jamb_labor_persons:  form.labor_manual ? parseInt(form.jamb_labor_persons) : undefined,
        jamb_labor_hours:    form.labor_manual ? parseFloat(form.jamb_labor_hours) : undefined,
        transport_fee:  form.transport_fee !== '' ? parseFloat(form.transport_fee) : undefined,
      });
      setCalc(data);
    } catch {}
  // Dep unica: el objeto form completo. Lista explicita RETIRADA 2026-07-20.
  // Omitia has_transom / transom_width_in / transom_height_in / transom_top_shape /
  // has_sidelights / sidelight_left_width_in / sidelight_right_width_in (migr 041)
  // y ademas has_trim / trim_sides / lites_delta / top_shape.
  // Efecto: el vendedor capturaba transom y el total en pantalla NO se movia,
  // pero handleSave SI los mandaba -> se guardaba un numero distinto al mostrado.
  // Con [form] la lista no puede volver a desincronizarse al agregar campos;
  // el debounce de 500ms del useEffect acota las llamadas.
  }, [form]);

  useEffect(() => {
    const t = setTimeout(runCalc, 500);
    return () => clearTimeout(t);
  }, [runCalc]);

  const handleSave = async () => {
    if (!form.design_id || !form.width_in || !form.height_in)
      return toast.error('Design, width, and height required');
    if (!isManager && parseFloat(form.disc_pct || 0) > 5)
      return toast.error('Discount above 5% requires manager approval');
    if (form.include_labor && form.labor_persons == null)
      return toast.error('Labor enabled but design not yet classified. Crew size will be set by Estimaciones.');
    setSaving(true);
    try {
      const payload = {
        ...form,
        category_id:    parseInt(form.category_id),
        design_id:      parseInt(form.design_id),
        width_in:       parseFloat(form.width_in),
        height_in:      parseFloat(form.height_in),
        disc_pct:       parseFloat(form.disc_pct || 0) / 100,
        glass_type_id:  form.glass_type_id ? parseInt(form.glass_type_id) : null,
        glass_width_in: form.glass_width_in ? parseFloat(form.glass_width_in) : null,
        glass_height_in:form.glass_height_in ? parseFloat(form.glass_height_in) : null,
        notch_qty:      parseInt(form.notch_qty || 0),
        template_qty:   parseInt(form.template_qty || 0),
        kit_id:         form.kit_id ? parseInt(form.kit_id) : null,
        trim_lf:        parseFloat(form.trim_lf || 0),
        has_trim:        form.has_trim,
        trim_sides:      parseInt(form.trim_sides || 2),
        lites_delta:     parseInt(form.lites_delta || 0),
        has_transom:    form.has_transom,
        transom_width_in:  form.transom_width_in ? parseFloat(form.transom_width_in) : null,
        transom_height_in: form.transom_height_in ? parseFloat(form.transom_height_in) : null,
        transom_top_shape: form.transom_top_shape || null,
        has_sidelights: form.has_sidelights,
        sidelight_left_width_in:  form.sidelight_left_width_in ? parseFloat(form.sidelight_left_width_in) : null,
        sidelight_right_width_in: form.sidelight_right_width_in ? parseFloat(form.sidelight_right_width_in) : null,
        jamb_width_in:  form.jamb_width_in ? parseFloat(form.jamb_width_in) : null,
        // H.17 (2026-07-23): identico al payload de runCalc. labor_hours NO
        // viaja nunca: si va, calcIron toma el camino viejo (horas x tarifa de
        // equipo) y no llega labor_detail -- el desglose de migr 076 se
        // guardaria vacio. El override viaja como crew_hours / crew_persons,
        // que SI pasan por resolveLabor y devuelven desglose completo.
        // La rama labor_hours de calcIron queda para el importador historico.
        labor_status:   form.labor_manual ? 'manual' : 'derived',
        labor_persons:  undefined,
        labor_hours:    undefined,
        crew_persons:        form.labor_manual ? parseInt(form.crew_persons) : undefined,
        crew_hours:          form.labor_manual ? parseFloat(form.crew_hours) : undefined,
        reinforce_persons:   form.labor_manual ? parseInt(form.reinforce_persons) : undefined,
        reinforce_hours:     form.labor_manual ? parseFloat(form.reinforce_hours) : undefined,
        jamb_labor_persons:  form.labor_manual ? parseInt(form.jamb_labor_persons) : undefined,
        jamb_labor_hours:    form.labor_manual ? parseFloat(form.jamb_labor_hours) : undefined,
        // El spread inicial ...form lo arrastra; no debe llegar al backend.
        labor_manual:   undefined,
        trip_fee:       parseFloat(form.trip_fee || 0),
        gas_fee:        parseFloat(form.gas_fee || 0),
        transport_fee:  form.transport_fee !== '' ? parseFloat(form.transport_fee) : null,
        finish:      form.finish?.trim() || null,
        opens:       form.opens || null,
        handle:      form.handle?.trim() || null,
        handle_side: b1IsDouble ? null : (form.handle_side || null),
        active_side: b1IsDouble ? (form.active_side || null) : null,
      };
      // Paso 3 familia (21-oct-2026): pieza de la otra familia (ver AluLineModal).
      if (isEdit) {
        await sendWithCrossFamilyAck(p => quotesAPI.updateIronLine(quoteId, editData.id, p), payload);
        toast.success('Iron door line updated');
      } else {
        await sendWithCrossFamilyAck(p => quotesAPI.addIronLine(quoteId, p),
          crossFamilyAck ? { ...payload, cross_family_ack: true } : payload);
        toast.success('Iron door line added');
      }
      onSaved();
    } catch (err) {
      if (err.cancelled) return;
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Shared style for read-only display fields
  const readOnlyFieldStyle = {
    background: 'var(--bg-3)',
    cursor: 'default',
    color: 'var(--text-2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-xl modal-flex" style={{ maxHeight: '92vh' }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Iron Door Line' : 'Add Iron Door Line'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>X</button>
        </div>

        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: 24 }}>

            {/* == LEFT - PRODUCT == */}
            <div>
              {/* Identification */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Line #</label>
                  <input className="form-input" placeholder="D1" value={form.line_number}
                    onChange={e => set('line_number', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input className="form-input" list="loc-options" placeholder="Front Entry"
                    value={form.location}
                    onChange={e => set('location', e.target.value)} />
                  <datalist id="loc-options">
                    <option value="Front Entry" />
                    <option value="Back Door" />
                    <option value="Patio" />
                    <option value="Side Entry" />
                    <option value="Office" />
                    <option value="Casita" />
                    <option value="Garage Entry" />
                    <option value="Courtyard" />
                    <option value="Wine Room" />
                    <option value="Interior" />
                  </datalist>
                </div>
              </div>

              {/* Category chips - un click, sin dropdown */}
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">Category *</label>
                <div className="chip-row">
                  {cats.map(c => (
                    <button key={c.id} type="button"
                      className={`chip${String(form.category_id) === String(c.id) ? ' active' : ''}`}
                      onClick={() => {
                        // H.7 (2026-07-20): al cambiar de CATEGORIA se limpia el bloque
                        // de ensamble y la forma del top. Antes sobrevivian: un transom
                        // de la categoria anterior seguia cobrandose, con su ancho
                        // autollenado del ancho viejo, y top_shape arrastraba su
                        // template_qty (vidrio templado sobre un diseno inexistente).
                        // NO se tocan medidas, ubicacion, acabado, herraje ni descuento:
                        // son datos del vano y del proyecto, no del producto.
                        setForm(f => ({
                          ...f,
                          category_id: String(c.id),
                          design_id: '',
                          has_transom: false, transom_width_in: '', transom_height_in: '', transom_top_shape: '',
                          has_sidelights: false, sidelight_left_width_in: '', sidelight_right_width_in: '',
                          top_shape: '', template_qty: 0,
                        }));
                        setKitTouched(false);
                        setDesignQuery('');
                      }}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Design picker - elegir viendo la puerta */}
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">Design *</label>
                {!form.category_id ? (
                  <div className="form-hint" style={{ padding: '14px 0' }}>
                    Pick a category above to browse designs.
                  </div>
                ) : form.design_id ? (
                  <button type="button" className="btn btn-ghost btn-sm"
                    onClick={() => { set('design_id', ''); setDesignQuery(''); }}
                    style={{ marginBottom: 4 }}>
                    Change design
                  </button>
                ) : (
                  <>
                    <input className="form-input" style={{ marginBottom: 8 }}
                      placeholder={`Search ${designs.length} designs...`}
                      value={designQuery}
                      onChange={e => setDesignQuery(e.target.value)} />
                    <div className="design-grid">
                      {filteredDesigns.length === 0 ? (
                        <div style={{ gridColumn: '1 / -1', padding: 16, textAlign: 'center',
                                      fontSize: 12, color: 'var(--text-3)' }}>
                          No designs match "{designQuery}"
                        </div>
                      ) : filteredDesigns.map(d => (
                        <DesignCard key={d.id} design={d}
                          selected={String(form.design_id) === String(d.id)}
                          onSelect={des => set('design_id', String(des.id))} />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Selected design detail */}
              {selectedDesign && <DesignDetail design={selectedDesign} showCostInfo={isManager}
                onShape={sh => set('top_shape', sh)} />}

              {/* Top Shape suggestion - driven by the design variant chosen above */}
              {(() => {
                const d = liteDefaults.find(x => x.top_shape === form.top_shape);
                return d && d.template_default > 0 ? (
                  <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-2)',
                       background: 'var(--bg-3)', borderRadius: 6, padding: '6px 10px' }}>
                    {form.top_shape.charAt(0).toUpperCase() + form.top_shape.slice(1)} top: suggested {d.template_default} template glass ({d.prevalence_pct}% of history, n={d.sample_n}) - editable below
                  </div>
                ) : null;
              })()}

              {/* Dimensions */}
              <div className="form-row-3" style={{ marginTop: 14 }}>
                <div className="form-group">
                  <label className="form-label">Width (in) *</label>
                  <input className="form-input text-mono" type="number" step="0.25" placeholder="36"
                    value={form.width_in} onChange={e => set('width_in', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Height (in) *</label>
                  <input className="form-input text-mono" type="number" step="0.25" placeholder="96"
                    value={form.height_in} onChange={e => set('height_in', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Discount %</label>
                  <input className="form-input text-mono" type="number" step="1" min="0"
                    max={isManager ? 100 : 5}
                    placeholder="0" value={form.disc_pct}
                    onChange={e => set('disc_pct', e.target.value)} />
                  {!isManager && (
                    <div className="form-hint">Up to 5% - higher needs manager</div>
                  )}
                </div>
              </div>

              {/* Glass */}
              <div style={{ background: 'var(--bg-3)', borderRadius: 8, padding: 14 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Glass</div>
                <div className="form-group">
                  <label className="form-label">Glass Type</label>
                  <SearchSelect
                    options={glassTypes}
                    value={form.glass_type_id}
                    onChange={v => set('glass_type_id', v)}
                    getImage={o => o.image_url}
                    placeholder="No glass / TBD - type to search"
                    emptyLabel="-  No glass / TBD"
                  />
                  {(() => {
                    const g = glassTypes.find(x => String(x.id) === String(form.glass_type_id));
                    if (!g) return null;
                    const m = (g.client_spec || '').match(/(\d+(?:\/\d+)?")\s*OA/i);
                    const oa = m ? m[1] : '5/8"';
                    return (
                      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-3)' }}>
                        {oa} OA - dual-pane insulated &amp; tempered ({oa === '5/8"' ? 'glass + air + glass' : 'two panes + air space'})
                      </div>
                    );
                  })()}
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-2)', lineHeight: 1.4 }}>
                    Glass area is calculated automatically per design (door area &times; model factor).
                    Leave the measurements blank to use the automatic calculation; enter them only to
                    force an exact glass size.
                  </div>
                </div>
                {form.glass_type_id && (
                  <div className="form-row">
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Glass Width (in)</label>
                      <input className="form-input text-mono" type="number" step="0.25"
                        value={form.glass_width_in} onChange={e => set('glass_width_in', e.target.value)} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Glass Height (in)</label>
                      <input className="form-input text-mono" type="number" step="0.25"
                        value={form.glass_height_in} onChange={e => set('glass_height_in', e.target.value)} />
                    </div>
                  </div>
                )}
                {form.glass_type_id && (() => {
                  const w = parseFloat(form.width_in) || 0, h = parseFloat(form.height_in) || 0;
                  const gw = parseFloat(form.glass_width_in) || 0, gh = parseFloat(form.glass_height_in) || 0;
                  const factor = parseFloat((selectedDesign && selectedDesign.glass_factor) || 0.8);
                  const ld = parseInt(form.lites_delta) || 0;
                  const doorSqft = (w * h) / 144;
                  const dn = (selectedDesign && selectedDesign.name) || 'design';
                  let sqft, expl;
                  if (gw && gh) {
                    sqft = (gw * gh) / 144;
                    expl = gw + 'x' + gh + ' = ' + sqft.toFixed(1) + ' sqft (explicit)';
                  } else {
                    sqft = doorSqft * factor;
                    expl = doorSqft.toFixed(1) + ' sqft x ' + factor + ' (' + dn + ') = ' + sqft.toFixed(1) + ' sqft';
                  }
                  const liteNote = ld ? ('Lites: ' + (ld > 0 ? '+' : '') + ld + ' (shown on quote, no price effect)') : '';
                  return (
                    <div style={{ marginTop: 8 }}>
                      <div className="form-row" style={{ alignItems: 'flex-end' }}>
                        <div className="form-group" style={{ marginBottom: 0, flex: '0 0 130px' }}>
                          <label className="form-label" style={{ fontSize: 11 }}>Lites +/- (vs design)</label>
                          <input className="form-input text-mono" type="number" step="1"
                            value={form.lites_delta} onChange={e => set('lites_delta', e.target.value)} />
                        </div>
                        <div style={{ flex: 1, fontSize: 11, color: 'var(--text-2)', paddingBottom: 6 }}>
                          Glass: {expl}
                        </div>
                      </div>
                      {liteNote && (
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, fontStyle: 'italic' }}>
                          {liteNote}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* == RIGHT - OPTIONS & TOTAL == */}
            <div>
              {/* Construction */}
              <div style={{ background: 'var(--bg-3)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Construction</div>
                <label className="flex items-center gap-2" style={{ cursor: 'pointer', marginBottom: 10, fontSize: 13 }}>
                  <input type="checkbox" checked={form.is_remodel}
                    onChange={e => set('is_remodel', e.target.checked)} />
                  Remodel (not new construction)
                </label>
                <label className="flex items-center gap-2" style={{ cursor: 'pointer', marginBottom: 10, fontSize: 13 }}>
                  <input type="checkbox" checked={form.hinge_type === 'pivot'}
                    onChange={e => set('hinge_type', e.target.checked ? 'pivot' : 'hinged')} />
                  Pivot door
                </label>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">
                    Construction Kit
                    {form.kit_id && !kitTouched && (
                      <span style={{ fontSize: 10, color: 'var(--accent)', marginLeft: 6 }}>(auto)</span>
                    )}
                  </label>
                  <SearchSelect
                    options={kits}
                    value={form.kit_id}
                    onChange={v => { set('kit_id', v); setKitTouched(true); }}
                    placeholder="None - type to search"
                  />
                </div>
              </div>

              {/* Extras */}
              <div style={{ background: 'var(--bg-3)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Extras & Charges</div>
                <div className="form-row">
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={!!form.has_trim}
                        onChange={e => {
                          const on = e.target.checked;
                          if (!on) setTrimTouched(false);   // H.16: al apagar, vuelve a auto
                          setForm(f => ({ ...f, has_trim: on,
                            trim_lf: on ? Math.round(trimLfFrom(f).lf * 10) / 10 : 0 }));
                        }} />
                      Trim
                    </label>
                    {form.has_trim && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <div style={{ flex: '0 0 92px' }}>
                          <label className="form-label" style={{ fontSize: 11 }}>Sides</label>
                          <select className="form-input" value={form.trim_sides}
                            onChange={e => {
                              const sides = parseInt(e.target.value);
                              setForm(f => {
                                const nf = { ...f, trim_sides: sides };
                                return { ...nf, trim_lf: Math.round(trimLfFrom(nf).lf * 10) / 10 };
                              });
                            }}>
                            <option value={2}>2 (in+out)</option>
                            <option value={1}>1</option>
                          </select>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label className="form-label" style={{ fontSize: 11 }}>LF (auto, editable)</label>
                          <input className="form-input text-mono" type="number" step="0.5" min="0"
                            value={form.trim_lf || ''}
                            onChange={e => { setTrimTouched(true); set('trim_lf', e.target.value); }} />
                        </div>
                      </div>
                    )}
                    {form.has_trim && (() => {
                      const T = trimLfFrom(form);
                      return (
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-2)' }}>
                          Trim: ({T.aw} + 2x{T.ah})/12 x {T.sides} side{T.sides > 1 ? 's' : ''} = {T.lf.toFixed(1)} LF
                          {T.hasAddons && ' (full opening: includes sidelights / transom)'}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Transom (add-on, migr 041) */}
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={!!form.has_transom}
                        onChange={e => {
                          const on = e.target.checked;
                          setForm(f => ({
                            ...f,
                            has_transom: on,
                            // H.20: al apagar se limpian las medidas. Antes se quedaban
                            // pegadas y reaparecian al re-activar el add-on.
                            transom_width_in:  on ? (f.transom_width_in || f.width_in || '') : '',
                            transom_height_in: on ? f.transom_height_in : '',
                            transom_top_shape: on ? f.transom_top_shape : '',
                          }));
                        }} />
                      Transom
                    </label>
                    {form.has_transom && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div style={{ flex: '0 0 90px' }}>
                          <label className="form-label" style={{ fontSize: 11 }}>Width (in)</label>
                          <input className="form-input text-mono" type="number" step="0.125" min="0"
                            value={form.transom_width_in} onChange={e => set('transom_width_in', e.target.value)} />
                        </div>
                        <div style={{ flex: '0 0 90px' }}>
                          <label className="form-label" style={{ fontSize: 11 }}>Height (in)</label>
                          <input className="form-input text-mono" type="number" step="0.125" min="0"
                            value={form.transom_height_in} onChange={e => set('transom_height_in', e.target.value)} />
                        </div>
                        <div style={{ flex: '0 0 120px' }}>
                          <label className="form-label" style={{ fontSize: 11 }}>Top shape</label>
                          <select className="form-input" value={form.transom_top_shape}
                            onChange={e => set('transom_top_shape', e.target.value)}>
                            <option value="">Square</option>
                            <option value="round">Round</option>
                            <option value="eyebrow">Eyebrow</option>
                            <option value="arch">Arch</option>
                          </select>
                        </div>
                      </div>
                    )}
                    {form.has_transom && form.transom_top_shape && (
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-2)' }}>
                        Shaped transom -&gt; add a templated glass under Glass cutouts.
                      </div>
                    )}
                  </div>

                  {/* Sidelights (add-on, migr 041) */}
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={!!form.has_sidelights}
                        onChange={e => {
                          const on = e.target.checked;
                          // H.20: al apagar se limpian los anchos (ver transom).
                          setForm(f => ({
                            ...f,
                            has_sidelights: on,
                            sidelight_left_width_in:  on ? f.sidelight_left_width_in  : '',
                            sidelight_right_width_in: on ? f.sidelight_right_width_in : '',
                          }));
                        }} />
                      Sidelights
                    </label>
                    {form.has_sidelights && (
                      <>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                          <div style={{ flex: 1 }}>
                            <label className="form-label" style={{ fontSize: 11 }}>Left width (in)</label>
                            <input className="form-input text-mono" type="number" step="0.125" min="0"
                              value={form.sidelight_left_width_in} onChange={e => set('sidelight_left_width_in', e.target.value)} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label className="form-label" style={{ fontSize: 11 }}>Right width (in)</label>
                            <input className="form-input text-mono" type="number" step="0.125" min="0"
                              value={form.sidelight_right_width_in} onChange={e => set('sidelight_right_width_in', e.target.value)} />
                          </div>
                        </div>
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-2)' }}>
                          Height inherits door height ({form.height_in || '?'}"); leave a side blank if absent.
                        </div>
                      </>
                    )}
                  </div>

                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label">Jamb Width (in)</label>
                    <input className="form-input text-mono" type="number" step="0.5" min="0"
                      placeholder=">8 adds charge"
                      value={form.jamb_width_in} onChange={e => set('jamb_width_in', e.target.value)} />
                    {parseFloat(form.jamb_width_in || 0) > 8 && (
                      <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>
                        Jamb {'>'}{parseFloat(form.jamb_width_in) > 10 ? '10' : '8'}": extra charge ${parseFloat(form.jamb_width_in) > 10 ? '6' : '3'}/sqft applied
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 8 }}>
                  <label className="form-label">Deadbolt</label>
                  <select className="form-select" value={form.deadbolt_type}
                    onChange={e => set('deadbolt_type', e.target.value)}>
                    <option value="none">None</option>
                    <option value="single">Single Cylinder - included in kit</option>
                    <option value="double">Double Cylinder - $18</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 8 }}>
                  <label className="form-label">Transport Fee ($)</label>
                  <input className="form-input text-mono" type="number" step="10" min="0"
                    placeholder="150"
                    value={form.transport_fee} onChange={e => set('transport_fee', e.target.value)} />
                  <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 4 }}>
                    Default $150. Melissa usa $100 en puertas chicas y $300 cuando hay dos viajes.
                  </div>
                </div>
                {/* B.1 Variables de producto (van al PDF y a Odoo) */}
                <div className="grid grid-2 gap-3">
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label">Finish</label>
                    <SearchSelect
                      options={colors}
                      value={(form.finish || '').startsWith('Custom') ? 'Custom' : form.finish}
                      onChange={v => set('finish', v === 'Custom' ? 'Custom: ' : v)}
                      getValue={o => o.name}
                      getLabel={o => o.name}
                      getSub={o => (o.finish_type === 'wood' ? 'Wood' : o.finish_type === 'custom' ? 'Special order' : 'Metal')}
                      getImage={o => o.image_url}
                      placeholder="Type to search..."
                      allowEmpty={true}
                      emptyLabel="-  None / TBD"
                    />
                    {(form.finish || '').startsWith('Custom') && (
                      <input className="form-input" style={{ marginTop: 6 }} placeholder="Describe custom finish..."
                        value={form.finish.replace(/^Custom:\s?/, '')}
                        onChange={e => set('finish', 'Custom: ' + e.target.value)} />
                    )}
                  </div>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label">Opens</label>
                    <select className="form-select" value={form.opens}
                      onChange={e => set('opens', e.target.value)}>
                      <option value="">--</option>
                      <option value="In">In</option>
                      <option value="Out">Out</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-2 gap-3">
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label">Handle</label>
                    <SearchSelect
                      options={handles}
                      value={(form.handle || '').startsWith('Custom') ? 'Custom' : form.handle}
                      onChange={v => set('handle', v === 'Custom' ? 'Custom: ' : v)}
                      getValue={o => o.name}
                      getLabel={o => o.name}
                      getImage={o => o.image_url}
                      thumbClass="thumb-tall"
                      zoomOnHover={true}
                      placeholder="Type to search..."
                      allowEmpty={true}
                      emptyLabel="-  None / TBD"
                    />
                    {(form.handle || '').startsWith('Custom') && (
                      <input className="form-input" style={{ marginTop: 6 }} placeholder="Describe custom handle..."
                        value={form.handle.replace(/^Custom:\s?/, '')}
                        onChange={e => set('handle', 'Custom: ' + e.target.value)} />
                    )}
                  </div>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label">{b1IsDouble ? 'Active Side (from outside)' : 'Handle Side (from outside)'}</label>
                    <select className="form-select"
                      value={b1IsDouble ? form.active_side : form.handle_side}
                      onChange={e => set(b1IsDouble ? 'active_side' : 'handle_side', e.target.value)}>
                      <option value="">--</option>
                      <option value="Left">Left</option>
                      <option value="Right">Right</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
                  {[
                    ['notch_qty',    'Glass w/ notch ($100 ea)'],
                    ['template_qty', 'Glass w/ template ($20 ea)'],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2" style={{ fontSize: 13 }}>
                      <span>{label}</span>
                      <input type="number" min="0" value={form[key]}
                        onChange={e => set(key, e.target.value)}
                        style={{ width: 60 }} />
                    </label>
                  ))}
                  <label className="flex items-center gap-2" style={{ cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={form.has_nailfin}
                      onChange={e => set('has_nailfin', e.target.checked)} />
                    Nailfin ($5/sf)
                  </label>
                </div>
              </div>

              {/* Labor - operational, mostly read-only */}
              <div style={{ background: 'var(--bg-3)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <label className="flex items-center gap-2"
                  style={{ cursor: noInstall ? 'not-allowed' : 'pointer',
                           opacity: noInstall ? 0.55 : 1,
                           marginBottom: form.include_labor ? 12 : 0, fontWeight: 600, fontSize: 13 }}>
                  <input type="checkbox" checked={form.include_labor} disabled={noInstall}
                    onChange={e => set('include_labor', e.target.checked)} />
                  Include Installation Labor
                  <span className="badge badge-green" style={{ fontWeight: 600 }}>Magnolia crew</span>
                </label>
                <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>
                  {noInstall
                    ? 'Quote is set to "No installation" - change the installer at quote level to add labor.'
                    : 'Iron doors are always installed by Magnolia crew.'}
                </div>
                {form.include_labor && !noInstall && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-3)', fontStyle: 'italic' }}>
                        Derived by Estimaciones. Not shown in customer PDF.
                      </div>
                      {laborDetail && laborDetail.labor_class && (
                        <span style={{ border: '1px solid #b8995f', borderRadius: 8, padding: '2px 10px',
                             fontSize: 11, color: '#b8995f', fontWeight: 600 }}>
                          {laborDetail.labor_class}
                        </span>
                      )}
                    </div>
                    {isManager && (
                      <label style={{ display: 'inline-flex', width: 'fit-content', alignItems: 'center', gap: 6, fontSize: 11,
                             marginBottom: 8, cursor: laborDetail ? 'pointer' : 'not-allowed',
                             opacity: laborDetail ? 1 : 0.5 }}>
                        <input type="checkbox" checked={!!form.labor_manual} disabled={!laborDetail}
                          onChange={e => set('labor_manual', e.target.checked)} />
                        Manual labor
                      </label>
                    )}
                      <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: -4, marginBottom: 8 }}>
                        {form.labor_manual
                          ? 'Editing crew, reinforcements and jamb by hand. Uncheck to return to derived values.'
                          : 'Values below are derived. Check "Manual labor" to edit them.'}
                      </div>
                    <div style={{ display: 'flex', gap: 6, fontSize: 10, color: 'var(--text-3)',
                         marginBottom: 2, paddingRight: 4 }}>
                      <span style={{ flex: 1 }} />
                      <span style={{ width: 58, textAlign: 'center' }}>Persons</span>
                      <span style={{ width: 12 }} />
                      <span style={{ width: 58, textAlign: 'center' }}>Hours</span>
                      <span style={{ width: 96 }} />
                    </div>
                    {[{ k: 'crew',  lbl: 'Crew base',      p: 'crew_persons',       h: 'crew_hours',       rt: 'crew_rate' },
                      { k: 'reinf', lbl: 'Reinforcements', p: 'reinforce_persons',  h: 'reinforce_hours',  rt: 'reinforce_rate' },
                      { k: 'jamb',  lbl: 'Jamb',           p: 'jamb_labor_persons', h: 'jamb_labor_hours', rt: 'jamb_labor_rate' }
                     ].map(r => (
                      <div key={r.k} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ flex: 1, fontSize: 11, color: 'var(--text-2)' }}>{r.lbl}</span>
                        <input className="form-input text-mono" type="number" min="0" step="1"
                          style={{ width: 58, textAlign: 'center', fontSize: 11, padding: '2px 4px' }}
                          value={form[r.p]} readOnly={!form.labor_manual}
                          onChange={e => set(r.p, e.target.value)} />
                        <span style={{ width: 12, textAlign: 'center', color: 'var(--text-3)' }}>x</span>
                        <input className="form-input text-mono" type="number" min="0" step="0.5"
                          style={{ width: 58, textAlign: 'center', fontSize: 11, padding: '2px 4px' }}
                          value={form[r.h]} readOnly={!form.labor_manual}
                          onChange={e => set(r.h, e.target.value)} />
                        <span style={{ width: 96, fontSize: 10, color: 'var(--text-3)', textAlign: 'right' }}>
                          {isManager && laborDetail && laborDetail[r.rt] != null
                            ? '$' + laborDetail[r.rt] + '/h' : ''}
                          {form.labor_manual && laborDetail && laborDetail.derived_from_scale
                            ? ' (der. ' + (laborDetail.derived_from_scale[r.p] || 0) + 'x'
                              + (laborDetail.derived_from_scale[r.h] || 0) + ')' : ''}
                        </span>
                      </div>
                     ))}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'baseline',
                         borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 6 }}>
                      <span style={{ flex: 1, fontSize: 11, color: 'var(--text-2)' }}>Total (2-person equiv)</span>
                      <span style={{ width: 58 }} />
                      <span style={{ width: 12 }} />
                      <span className="text-mono" style={{ width: 58, textAlign: 'center', fontSize: 13, fontWeight: 600 }}>
                        {laborHoursEquiv != null ? laborHoursEquiv + ' h' : '-'}
                      </span>
                      <span style={{ width: 96 }} />
                    </div>
                    {laborDetail && laborDetail.hours_reference && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10,
                           color: 'var(--text-3)', marginTop: 2 }}>
                        <span>Suggested range</span>
                        <span className="text-mono">
                          {laborDetail.hours_reference.min} - {laborDetail.hours_reference.max} h
                        </span>
                        <span style={{ width: 96 }} />
                      </div>
                    )}
                    {laborDetail && laborDetail.warnings && laborDetail.warnings.length > 0 && (
                      <div style={{ fontSize: 10, color: '#b45309', fontWeight: 600, marginTop: 6 }}>
                        {laborDetail.warnings.join(' - ')}
                      </div>
                    )}
                    {!isManager && (
                      <div style={{ fontSize: 11, color: '#b45309', fontWeight: 600, marginTop: 6 }}>
                        Preliminary estimate - Estimaciones confirms the final hours.
                      </div>
                    )}
                  </div>
                )}
                {/* Aprobacion de labor (migr 076). FUERA del guard de
                    include_labor: el estado debe verse aunque la instalacion
                    este apagada. Aprueba HORAS, no el precio de la linea. */}
                {isManager && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                       marginTop: 10, paddingTop: 8, borderTop: '1px dashed var(--border)', gap: 8 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-3)' }}>
                      {isLaborApproved
                        ? 'Labor approved' + (laborApproval.by_name ? ' by ' + laborApproval.by_name : '')
                          + (laborApproval.approved_at
                              ? ' - ' + new Date(laborApproval.approved_at).toLocaleDateString() : '')
                        : (editData?.id ? 'Labor not approved by Estimaciones' : 'Save the line to approve')}
                    </span>
                    <button type="button"
                      className={isLaborApproved ? 'btn btn-ghost btn-sm' : 'btn btn-primary btn-sm'}
                      disabled={!editData?.id || approving}
                      onClick={toggleLaborApproval}
                      title={!editData?.id ? 'The line must be saved first' : ''}>
                      {approving ? '...' : (isLaborApproved ? 'Remove approval' : 'Approve labor')}
                    </button>
                  </div>
                )}
              </div>

              {/* Live total - PRECIO CLIENTE (sale-side). Manager/admin ven
                  ademas la columna de COSTO por componente, lado a lado
                  (decision Mau 2026-06-06). El vendor solo recibe sale. */}
              {/* Peso estimado + alertas de crew/equipo (informativo, todos los roles) */}
              {estWeight != null && estWeight > 0 && (
                <div style={{
                  fontSize: 12, padding: '8px 12px', borderRadius: 6, marginBottom: 8,
                  background: estWeight >= 800 ? '#fef2f2' : estWeight >= 550 ? '#fffbeb' : 'var(--bg-3)',
                  border: `1px solid ${estWeight >= 800 ? '#fca5a5' : estWeight >= 550 ? '#fcd34d' : 'var(--border)'}`,
                  color: estWeight >= 800 ? '#b91c1c' : estWeight >= 550 ? '#b45309' : 'var(--text-2)',
                }}>
                  Est. weight: ~{Math.round(estWeight)} lbs (iron)
                  {selectedDesign?.weight_lbs_per_sqft != null &&
                    ` (${parseFloat(selectedDesign.weight_lbs_per_sqft).toFixed(1)} lbs/sqft)`}
                  {estWeight >= 800 && ' - Very heavy: verify site access / equipment with Operations'}
                  {estWeight >= 550 && estWeight < 800 && ' - Heavy door: verify access'}
                  {calc?.weight_glass > 0 && (
                    <div style={{ marginTop: 4, fontSize: 11, opacity: 0.85 }}>
                      Glass: ~{Math.round(calc.weight_glass)} lbs — ships and installs separately
                    </div>
                  )}
                </div>
              )}
              <div style={{ minHeight: 280 }}>
              {!calc?.sale && (
                <div style={{ background: 'var(--bg-3)', borderRadius: 8, padding: 14,
                     fontSize: 12, color: 'var(--text-3)', textAlign: 'center', minHeight: 60,
                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Enter design, width &amp; height to see pricing&hellip;
                </div>
              )}
              {calc?.sale && (() => {
                // El Vendedor no ve estos renglones: Tariffs es costo de
                // importacion, Kit y Transport son vocabulario interno, y
                // Deadbolt es un renglon de $29 junto a uno de $3,500.
                // Sus montos se suman a Door: el total no cambia.
                const HIDDEN_FROM_VENDOR = ['Transport', 'Tariffs', 'Kit', 'Deadbolt'];
                const rows = [
                  ['Door',     calc.door_net,                                        calc.sale.door],
                  ['Glass',    calc.glass_total,                                     calc.sale.glass],
                  ['Trim',     calc.trim_total,                                      calc.sale.trim],
                  ['Deadbolt', calc.deadbolt_cost,                                   calc.sale.deadbolt],
                  ['Extras',   (calc.template_total || 0) + (calc.glass_notch_total || 0), calc.sale.extras],
                  ['Jamb',     calc.jamb_charge,                                     calc.sale.jamb],
                  ['Pivot',    calc.pivot_charge,                                    calc.sale.pivot],
                  ['Nailfin',  calc.nailfin_charge,                                  calc.sale.nailfin],
                  ['Tariffs',  calc.tariffs_cost_total,                              calc.sale.tariffs],
                  ['Kit',      calc.kit_cost,                                        calc.sale.kit],
                  ['Transport',calc.transport_fee,                                   calc.sale.transport],
                  ['Labor',    calc.labor_total,                                     calc.sale.labor],
                  ['Fees',     (calc.trip_fee || 0) + (calc.gas_fee || 0),           calc.sale.fees],
                ].map(([label, c, s]) => (!isManager && label === 'Door')
                  ? [label, c, (s || 0) + (calc.sale.transport || 0)
                                        + (calc.sale.tariffs   || 0)
                                        + (calc.sale.kit       || 0)
                                        + (calc.sale.deadbolt  || 0)]
                  : [label, c, s])
                 .filter(([label, c, s]) => ((s || 0) > 0 || (c || 0) > 0)
                  && (isManager || !HIDDEN_FROM_VENDOR.includes(label)));
                const gridCols = isManager ? '1fr auto auto' : '1fr auto';
                return (
                <div style={{ background: 'var(--accent-glow)', border: '1px solid var(--accent)', borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 8, fontWeight: 600 }}>
                    Estimated Customer Price
                  </div>
                  {/* H.8 (2026-07-20): area del ENSAMBLE desglosada. El motor ya
                      devolvia assembly_sqft pero la UI nunca lo pintaba: el vendedor
                      no podia auditar sobre que superficie se le cobra al cliente. */}
                  {(() => {
                    const w = parseFloat(form.width_in) || 0, h = parseFloat(form.height_in) || 0;
                    const doorSf = (w * h) / 144;
                    const tSf = (form.has_transom && form.transom_width_in && form.transom_height_in)
                      ? (parseFloat(form.transom_width_in) * parseFloat(form.transom_height_in)) / 144 : 0;
                    const slW = form.has_sidelights
                      ? (parseFloat(form.sidelight_left_width_in) || 0) + (parseFloat(form.sidelight_right_width_in) || 0) : 0;
                    const slSf = (slW * h) / 144;
                    const localSum = doorSf + tSf + slSf;
                    const asm = calc.assembly_sqft != null ? parseFloat(calc.assembly_sqft) : localSum;
                    const parts = [`${doorSf.toFixed(2)} door`];
                    if (tSf > 0)  parts.push(`${tSf.toFixed(2)} transom`);
                    if (slSf > 0) parts.push(`${slSf.toFixed(2)} sidelights`);
                    const drift = Math.abs(asm - localSum) > 0.01;
                    return (
                      <div style={{ fontSize: 11, color: drift ? "var(--danger, #dc2626)" : "var(--text-2)",
                           marginBottom: 8, paddingBottom: 6, borderBottom: "1px dashed var(--border)" }}>
                        Area: {parts.join(" + ")} = <b>{asm.toFixed(2)} sqft</b>
                        {drift && ` (!) motor dice ${asm.toFixed(2)}, UI suma ${localSum.toFixed(2)}`}
                      </div>
                    );
                  })()}
                  <div style={{ display: "none" }}>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: gridCols, columnGap: 14, rowGap: 4, fontSize: 12 }}>
                    {isManager && (
                      <>
                        <span />
                        <span style={{ color: 'var(--text-3)', fontSize: 10, textAlign: 'right' }}>Cost</span>
                        <span style={{ color: 'var(--text-3)', fontSize: 10, textAlign: 'right' }}>Price</span>
                      </>
                    )}
                    {rows.map(([label, cost, salePrice]) => (
                      <span key={label} style={{ display: 'contents' }}>
                        <span className="text-muted">{label}</span>
                        {isManager && (
                          <span className="text-mono" style={{ color: 'var(--text-3)', textAlign: 'right' }}>
                            {fmt.currency(cost || 0)}
                          </span>
                        )}
                        <span className="text-mono" style={{ textAlign: 'right' }}>{fmt.currency(salePrice)}</span>
                      </span>
                    ))}
                  </div>
                  <hr className="divider" style={{ margin: '8px 0' }} />
                  <div style={{ display: 'grid', gridTemplateColumns: gridCols, columnGap: 14, alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Total</span>
                    {isManager && (
                      <span className="text-mono" style={{ color: 'var(--text-3)', textAlign: 'right', fontSize: 13 }}>
                        {fmt.currency(calc.line_total || 0)}
                      </span>
                    )}
                    <span className="text-mono" style={{ fontSize: 18, fontWeight: 700, textAlign: 'right' }}>
                      {fmt.currency(calc.sale.line_total)}
                    </span>
                  </div>
                  {calc.sale.price_sf != null && (
                    isManager ? (
                      (() => {
                        const costNoFees = (calc.line_total || 0) - (calc.trip_fee || 0) - (calc.gas_fee || 0);
                        // H.9 (2026-07-20): denominador = ENSAMBLE, no puerta sola.
                        // Antes: costNoFees / calc.sqft. Como price_sf del motor usa
                        // assembly_area, Cost/sf y Price/sf quedaban con denominadores
                        // distintos y el margen se leia al reves con transom/sidelights.
                        const asm_sf  = calc.assembly_sqft || calc.sqft;
                        const cost_sf = asm_sf > 0 ? costNoFees / asm_sf : null;
                        const margin = calc.sale.line_total - (calc.line_total || 0);
                        const margin_pct = calc.sale.line_total > 0 ? margin / calc.sale.line_total : 0;
                        return (
                          <>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', columnGap: 14, marginTop: 2 }}>
                              <span />
                              <span className="text-mono" style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'right' }}>
                                {cost_sf != null ? `${fmt.currency(cost_sf)}/sf` : ''}
                              </span>
                              <span className="text-mono" style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'right' }}>
                                {fmt.currency(calc.sale.price_sf)}/sf
                              </span>
                            </div>
                            <div className="flex justify-between" style={{ fontSize: 11, marginTop: 8,
                                 paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
                              <span style={{ color: 'var(--text-3)', fontWeight: 600 }}>Margin</span>
                              <span className="text-mono" style={{ color: 'var(--green)', fontWeight: 700 }}>
                                {fmt.currency(margin)} ({(margin_pct * 100).toFixed(1)}%)
                              </span>
                            </div>
                          </>
                        );
                      })()
                    ) : (
                      <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'right', marginTop: 2 }}>
                        {fmt.currency(calc.sale.price_sf)}/sf
                      </div>
                    )
                  )}
                </div>
                );
              })()}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer flex justify-between items-center">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Line' : 'Add Iron Door Line'}
          </button>
        </div>
      </div>
    </div>
  );
}
